import React, { useState, useEffect } from 'react';

// Components
import WpTextField from '../text-field/WpTextField';
import {
  WpDropdown,
  WpDropdownMenu,
  WpDropdownToggle,
  WpDropdownMenuItem,
  WidthType,
  DropdownPlacement as DropdownPlacementType,
} from '../dropdown';

// Styles
import './WpSelect.scss';

export interface StrictOption {
  /** Machine value of the option; this is the value passed to `onChange` */
  value: string;
  /** Human-readable text for the option */
  label: string;
  /** Option will be visible, but not selectable */
  disabled?: boolean;
  hideLabel?: boolean;
  /** Allow other data for custom option */
  [x: string]: any;
}

export type DropdownPlacement = DropdownPlacementType;
export type SelectOption = string | StrictOption;
export { WidthType };

export interface Props {
  /** List of options or option groups to choose from */
  options?: SelectOption[];
  /** Label for the select */
  label: string;
  /** Visually hide the label */
  labelHidden?: boolean;
  /** Disable input */
  disabled?: boolean;
  /** Additional text to aide in use */
  helpText?: React.ReactNode;
  /** Example text to display as placeholder */
  placeholder?: string;
  /** ID for form input */
  id: string;
  /** Name for form input */
  name?: string;
  /** Value for form input */
  value?: string;
  /** Display an error state */
  error?: string;
  /** Callback when selection is changed */
  onChange?(selected: string | StrictOption): void;
  /** Callback when select is opened */
  onOpen?(): void;
  /** Callback when select is focussed */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  required?: boolean;
  prefixIcon?: string;
  isSearchable?: boolean;
  searchPlaceholder?: string;
  resetOnSelect?: boolean;
  optionsWidth?: WidthType;
  closeOnClick?: boolean;
  components?: {
    option?: Function;
    innerDropdown?: Function;
    dropdownFooter?: Function;
  };
  // when the component is in a controlled state
  // need an option to pass a new value in and update the local state
  forceReset?: boolean;
  placement?: DropdownPlacement;
  showRequiredIndicator?: boolean;
  qaTest?: string;
  emptyOptionsMessage?: string;
}

const DEFAULT_PLACEHOLDER_VALUE = '';

export const WpSelectV2: React.FC<Props> = ({
  options: optionsProp,
  label,
  labelHidden,
  disabled,
  helpText,
  placeholder,
  id,
  value = DEFAULT_PLACEHOLDER_VALUE,
  error,
  onChange,
  onOpen,
  onFocus,
  onBlur,
  required,
  components,
  prefixIcon,
  optionsWidth = WidthType.target,
  resetOnSelect = false,
  isSearchable = false,
  forceReset = false,
  closeOnClick = true,
  showRequiredIndicator = true,
  placement,
  searchPlaceholder = 'Filter options',
  emptyOptionsMessage = 'No options',
  qaTest,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [searchString, setSearchString] = useState('');

  const baseClassName = 'WpSelect';

  function handleChange(option: StrictOption) {
    if (onChange) {
      if (components?.option) onChange(option);
      else onChange(option.value);
    }
    if (resetOnSelect) {
      setSelectedValue(null);
    } else {
      setSelectedValue(option);
    }
    setSearchString('');
  }

  function normalizeStringOption(option: string): StrictOption {
    return {
      label: option,
      value: option,
      disabled: false,
    };
  }

  /**
   * Converts a string option (and each string option in a Group) into
   * an Option object.
   */

  function normalizeOption(option: SelectOption): StrictOption {
    if (typeof option === 'string') {
      return normalizeStringOption(option);
    } else {
      return option;
    }
  }
  const options = optionsProp || [];
  const normalizedOptions = options.map(normalizeOption);

  const filteredOptions = () => {
    return normalizedOptions.filter(option => option.label.toLowerCase().includes(searchString.toLowerCase()));
  };

  const handleSearchKeyDown = event => {
    // this fake click closees the dropdown after a selection is made
    const fakeClick = () => {
      const el = document.createElement('div');
      document.body.appendChild(el);
      el.click();
      document.body.removeChild(el);
    };

    // const options = filteredOptions;
    const options = filteredOptions();
    if (event.key === 'Enter' && options.length > 0 && !options[0].disabled) {
      const selectedOption = options[0];
      handleChange(selectedOption);
      fakeClick();
    }
  };

  function renderSearchOption() {
    return (
      <li className={`${baseClassName}__search`}>
        <WpTextField
          id="dropdown_search_input"
          label={`Search ${label}`}
          placeholder={searchPlaceholder}
          labelHidden
          prefixIcon="search"
          autoFocus
          onChange={value => setSearchString(value)}
          onKeyDown={handleSearchKeyDown}
          hideBorder
          value={searchString}
        />
      </li>
    );
  }

  function renderSingleOption(option: StrictOption): React.ReactNode {
    const { value, label } = option;
    const CustomOption = components?.option;
    return (
      <WpDropdownMenuItem
        key={value}
        qaTest={value}
        onClick={() => handleChange(option)}
        disabled={option.disabled}
        closeOnClick={closeOnClick}
      >
        {CustomOption ? <CustomOption option={option} /> : label}
      </WpDropdownMenuItem>
    );
  }

  // const footer = (): React.ReactNode | null => {
  //   if (!components?.dropdownFooter) return null;

  //   return components.dropdownFooter();
  // };

  const optionsMarkup = () => {
    if (normalizedOptions.length === 0)
      return (
        <WpDropdownMenuItem disabled>
          <div className={`${baseClassName}__search__empty`}>{emptyOptionsMessage}</div>
        </WpDropdownMenuItem>
      );
    if (!isSearchable || (isSearchable && searchString === '')) return normalizedOptions.map(renderSingleOption);

    const filtered = filteredOptions();
    const showSearchEmptyState = filtered.length === 0;

    if (showSearchEmptyState) {
      return (
        <WpDropdownMenuItem disabled>
          <div className={`${baseClassName}__search__empty`}>No Matches</div>
        </WpDropdownMenuItem>
      );
    } else {
      return filtered.map(renderSingleOption);
    }
  };

  useEffect(() => {
    if ((value && !selectedValue) || forceReset) {
      // (josh) - preserve this line for now until i'm confident in fix
      // if (value !== selectedValue || forceReset) {
      // handleChange(normalizedOptions.find(option => option.value === value));
      setSelectedValue(normalizedOptions.find(option => option.value === value));
    }
  }, [value, selectedValue, normalizedOptions, forceReset]);

  const CustomInnerMark = () => {
    const CustomInner = components?.innerDropdown;
    return <CustomInner selected={selectedValue} />;
  };

  const InnerMarkup = components?.innerDropdown ? (
    CustomInnerMark()
  ) : (
    <WpTextField
      id={id}
      label={label}
      value={selectedValue && !selectedValue.hideLabel ? selectedValue.label : ''}
      placeholder={placeholder}
      postfixIcon="chevron-down"
      disabled={disabled}
      required={required}
      helpText={helpText}
      labelHidden={labelHidden}
      error={error}
      prefixIcon={prefixIcon}
      onFocus={onFocus}
      onBlur={onBlur}
      readOnly
      showRequiredIndicator={showRequiredIndicator}
      qaTest={qaTest}
    />
  );

  return (
    <WpDropdown disabled={disabled} onClickCallback={onOpen} placement={placement}>
      <WpDropdownToggle>{InnerMarkup}</WpDropdownToggle>
      <WpDropdownMenu components={{ footer: components?.dropdownFooter }} width={optionsWidth}>
        {isSearchable && renderSearchOption()}
        {optionsMarkup()}
      </WpDropdownMenu>
    </WpDropdown>
  );
};

export default WpSelectV2;
